import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { LeaveCalendar } from '../../../../models/api/view/claim/summary/leavecalendar';

interface WeeklyRecord {
  label: string;
  sun: string;
  mon: string;
  tue: string;
  wed: string;
  thu: string;
  fri: string;
  sat: string;
}

const fmt = (n: number) => (n > 0 ? n.toFixed(2) : '0');

/**
 * Component that displays leave calendar data in a weekly view.
 * Groups absences by week and sums hours per day.
 */
@Component({
  selector: 'app-leave-calendar-weekly',
  imports: [TableComponent, ColumnComponent],
  template: `
    <div class="tw-italic tw-py-2">
      Weekly view shows the hours missed per day for the employee for the
      selected leave claim.
    </div>
    <app-table
      #table
      [dataSource]="items"
      [pageSize]="10"
      [scrollable]="false"
      [showFilter]="false"
      recordLabel="week"
    >
      <app-column name="label" label="Week" />
      <app-column name="sun" label="Sunday" />
      <app-column name="mon" label="Monday" />
      <app-column name="tue" label="Tuesday" />
      <app-column name="wed" label="Wednesday" />
      <app-column name="thu" label="Thursday" />
      <app-column name="fri" label="Friday" />
      <app-column name="sat" label="Saturday" />
    </app-table>
  `,
  styles: ``,
})
export class LeaveCalendarWeeklyComponent implements OnChanges {
  @Input() absences: LeaveCalendar[] = [];
  @Input() loading = false;

  items: WeeklyRecord[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['absences']) {
      this.items = this.generateWeeklyRecords();
    }
  }

  private generateWeeklyRecords(): WeeklyRecord[] {
    if (!this.absences || this.absences.length === 0) {
      return [];
    }

    // Group absences by week
    const weekMap = new Map<string, { label: string; days: number[] }>();
    const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

    this.absences.forEach((absence) => {
      const date = new Date(absence.date);
      const weekKey = this.getWeekKey(date);

      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, {
          label: this.getWeekLabel(date),
          days: [0, 0, 0, 0, 0, 0, 0],
        });
      }

      const week = weekMap.get(weekKey)!;
      const hoursValue = parseFloat(absence.hoursAbsent) || 0;
      const minutesValue = parseFloat(absence.minutesAbsent) || 0;
      week.days[date.getDay()] += hoursValue + minutesValue / 60;
    });

    // Convert to formatted records and sort
    return Array.from(weekMap.values())
      .map(
        (w) =>
          ({
            label: w.label,
            ...Object.fromEntries(dayKeys.map((k, i) => [k, fmt(w.days[i])])),
          }) as WeeklyRecord,
      )
      .sort(
        (a, b) =>
          this.parseWeekLabel(a.label).getTime() -
          this.parseWeekLabel(b.label).getTime(),
      );
  }

  /**
   * Gets a unique key for the week that the date falls in.
   * Week starts on Sunday.
   */
  private getWeekKey(date: Date): string {
    const sunday = this.getSundayOfWeek(date);
    return sunday.toISOString().split('T')[0];
  }

  /**
   * Gets the Sunday of the week for a given date.
   */
  private getSundayOfWeek(date: Date): Date {
    const day = date.getDay();
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - day);
    sunday.setHours(0, 0, 0, 0);
    return sunday;
  }

  /**
   * Generates a label for the week (e.g., "1/1/2025-1/7/2025")
   */
  private getWeekLabel(date: Date): string {
    const sunday = this.getSundayOfWeek(date);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);

    return `${this.formatDate(sunday)}-${this.formatDate(saturday)}`;
  }

  /**
   * Formats a date as M/D/YYYY
   */
  private formatDate(date: Date): string {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  /**
   * Parses the start date from a week label
   */
  private parseWeekLabel(label: string): Date {
    const startDateStr = label.split('-')[0];
    const [month, day, year] = startDateStr
      .split('/')
      .map((n) => parseInt(n, 10));
    return new Date(year, month - 1, day);
  }
}
